import React, {
  FC, ReactNode, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@mdi/react';
import { mdiClose } from '@mdi/js';
import cx from 'clsx';
import { useLocale } from 'lib';
import styles from './style.module.scss';

type GuideTooltipProps = {
  /** What the arrow points at. Falls back to `fallback` when it is gone from the page. */
  target: HTMLElement | null;
  fallback: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
};

type Position = { top: number; left: number; arrow: number; above: boolean };

const GAP = 12;
const MARGIN = 8;

/**
 * Tooltip with an arrow that follows its target while it is open.
 * ponytail: re-measures every animation frame (a few getBoundingClientRect calls) instead of
 * observing scroll / resize / layout; fine while one tooltip is open, switch to observers if that changes.
 */
export const GuideTooltip: FC<GuideTooltipProps> = ({
  target, fallback, onClose, children,
}) => {

  const { t } = useLocale();
  const boxRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>(null);

  useEffect(() => {

    const anchor = target?.isConnected ? target : fallback;

    if (!anchor) return;

    // Only scroll when the target is out of sight; scrolling an already visible target makes the whole layout shift.
    const { top, bottom } = anchor.getBoundingClientRect();

    if (top < 0 || bottom > window.innerHeight) anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });

  }, [target, fallback]);

  useEffect(() => {

    let frame = 0;

    const track = () => {

      frame = requestAnimationFrame(track);

      const anchor = target?.isConnected ? target : fallback;
      const box = boxRef.current;

      if (anchor && box) {

        const rect = anchor.getBoundingClientRect();
        const { width, height } = box.getBoundingClientRect();
        const above = rect.bottom + GAP + height > window.innerHeight && rect.top - GAP - height > 0;
        const anchorX = rect.left + Math.min(rect.width / 2, 48);
        const left = Math.round(Math.min(Math.max(anchorX - width / 2, MARGIN), window.innerWidth - width - MARGIN));
        const next: Position = {
          top: Math.round(above ? rect.top - GAP - height : rect.bottom + GAP),
          left,
          arrow: Math.round(anchorX - left),
          above,
        };

        setPosition((prev) => (prev && Object.keys(next).every((key) => prev[key] === next[key]) ? prev : next));

      }

    };

    track();

    return () => cancelAnimationFrame(frame);

  }, [target, fallback]);

  return createPortal(
    <div
      ref={boxRef}
      role="dialog"
      className={cx(styles.tooltip, position?.above ? styles.above : styles.below)}
      style={position ? { top: position.top, left: position.left, '--arrow': `${position.arrow}px` } as React.CSSProperties : { visibility: 'hidden' }}
    >
      <div className={styles.content}>{children}</div>
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label={t('Alarm_Detail_Response_Procedure.Guide.Close')}
        title={t('Alarm_Detail_Response_Procedure.Guide.Close')}
      >
        <Icon path={mdiClose}/>
      </button>
    </div>,
    document.body,
  );

};
